import { AuthContextType } from '@auth/AuthProvider';
import NetworkController, { HTTPMethod, HTTPRequest, HTTPResponse } from '@data/controller/NetworkController';
import { ExternalVendor } from '@interfaces/external-vendor.interface';
import {
  ImportableTeam,
  IntegrationConnection,
  PeopleImportJob,
  PeopleImportMode
} from '@interfaces/integration.interface';
import { TeamImportResult } from '@data/interfaces/Volunteering/volunteerteam.interface';

export type IntegrationAuthorizationURLResponse = {
  result: {
    authorizationURL: string;
  }
};

/// The Sanctuary integration routes. The provider path segment is matched
/// case-insensitively with dashes read as underscores, so the vendor's own
/// value (PLANNING_CENTER) is what goes in the URL.
export default class IntegrationsController {
  private static baseURL: string = process.env.REACT_APP_SANCTUARY_API_URL || "";

  static async fetchIntegrations(
    churchID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: IntegrationConnection[] }>> {
    return await NetworkController.performRequestAt<{ result: IntegrationConnection[] }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchID}/integrations`),
      auth.consumeRefreshToken
    );
  }

  // Only for providers connected through a redirect: the API answers an
  // API-key provider with a 400 rather than a URL.
  static async fetchAuthorizationURL(
    churchID: string,
    provider: ExternalVendor,
    auth: AuthContextType
  ): Promise<HTTPResponse<IntegrationAuthorizationURLResponse>> {
    return await NetworkController.performRequestAt<IntegrationAuthorizationURLResponse>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.GET,
        `/v1/church/${churchID}/integrations/${provider}/authorization-url`
      ),
      auth.consumeRefreshToken
    );
  }

  // Re-submitting is how a church rotates a key, so this overwrites an
  // existing connection rather than failing.
  static async saveAPIKey(
    churchID: string,
    provider: ExternalVendor,
    apiKey: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: IntegrationConnection }>> {
    return await NetworkController.performRequestAt<{ result: IntegrationConnection }>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.PUT,
        `/v1/church/${churchID}/integrations/${provider}/api-key`,
        { apiKey }
      ),
      auth.consumeRefreshToken
    );
  }

  // Returns straight away with the queued job; the work happens off a queue,
  // and progress comes from fetchPeopleImport. The API defaults an empty body
  // to a preview, so the mode is always sent explicitly.
  static async startPeopleImport(
    churchID: string,
    provider: ExternalVendor,
    mode: PeopleImportMode,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: PeopleImportJob }>> {
    return await NetworkController.performRequestAt<{ result: PeopleImportJob }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.POST, `/v1/church/${churchID}/integrations/${provider}/imports`, { mode }),
      auth.consumeRefreshToken
    );
  }

  // Newest first. Job records expire after six months, so an empty list
  // doesn't prove a church never imported -- starting again will say so.
  static async fetchPeopleImports(
    churchID: string,
    provider: ExternalVendor,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: PeopleImportJob[] }>> {
    return await NetworkController.performRequestAt<{ result: PeopleImportJob[] }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchID}/integrations/${provider}/imports`),
      auth.consumeRefreshToken
    );
  }

  static async fetchPeopleImport(
    churchID: string,
    provider: ExternalVendor,
    jobID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: PeopleImportJob }>> {
    return await NetworkController.performRequestAt<{ result: PeopleImportJob }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchID}/integrations/${provider}/imports/${jobID}`),
      auth.consumeRefreshToken
    );
  }

  // Read live from the provider on every call, so it can take a moment.
  static async fetchImportableTeams(
    churchID: string,
    provider: ExternalVendor,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: ImportableTeam[] }>> {
    return await NetworkController.performRequestAt<{ result: ImportableTeam[] }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchID}/integrations/${provider}/teams`),
      auth.consumeRefreshToken
    );
  }

  // A one-time copy into a new Salt team. Refused with TEAM_ALREADY_IMPORTED
  // for a team imported before.
  static async importTeam(
    churchID: string,
    provider: ExternalVendor,
    externalTeamID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: TeamImportResult }>> {
    return await NetworkController.performRequestAt<{ result: TeamImportResult }>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.POST,
        `/v1/church/${churchID}/integrations/${provider}/teams/${encodeURIComponent(externalTeamID)}/import`
      ),
      auth.consumeRefreshToken
    );
  }

  static async disconnect(
    churchID: string,
    provider: ExternalVendor,
    auth: AuthContextType
  ): Promise<HTTPResponse<unknown>> {
    return await NetworkController.performRequestAt<unknown>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.DELETE, `/v1/church/${churchID}/integrations/${provider}`),
      auth.consumeRefreshToken
    );
  }
}
